import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Trash2, Loader2, Download, FileText, Copy } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function MaintenancePanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup/seed-stats");
      setStats(data);
    } catch (e) {
      toast.error("Could not load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  const doCleanup = async (remove_vendors_only) => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup/seed-data", {
        confirm: true,
        remove_vendors_only,
      });
      toast.success(
        `Cleanup done — ${data.vendors_deleted} vendors, ${data.users_deleted} users, ${data.chats_deleted} chats removed`
      );
      await loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <Card className="p-6 border-border/60">
      <h2 className="font-heading text-xl flex items-center gap-2">
        <Trash2 className="h-5 w-5" /> Seeded data cleanup
      </h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
        Removes the synthetic vendors, consumers, chats, reviews, orders and cart items
        created by <code className="px-1 rounded bg-muted">seed_data.run_seed</code>.
        QA fixture accounts (admin-qa-1, qa-consumer-1, qa-consumer-3) and{" "}
        <code className="px-1 rounded bg-muted">vendor_qa_iter4</code> are always preserved.
      </p>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading current counts…
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
          {[
            ["Users", stats.seeded_users],
            ["Vendors", stats.seeded_vendors],
            ["Reviews", stats.seeded_reviews],
            ["Chats", stats.seeded_chats],
            ["Orders", stats.seeded_orders],
            ["Cart items", stats.seeded_cart_items],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-border/60 p-3" data-testid={`maint-count-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
              <div className="font-heading text-2xl mt-1">{value?.toLocaleString?.() ?? value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button variant="outline" size="sm" onClick={loadStats} disabled={loading} data-testid="maint-refresh-btn">
          <Loader2 className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : "hidden"}`} /> Refresh counts
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={cleaning || !stats?.seeded_vendors} data-testid="maint-clean-vendors-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clean seeded vendors only
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove all seeded vendors?</AlertDialogTitle>
              <AlertDialogDescription>
                Deletes the {stats?.seeded_vendors?.toLocaleString?.()} synthetic vendor profiles
                plus their reviews, chats, orders and cart items. The seeded consumer accounts will be kept.
                QA fixtures are preserved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="maint-clean-vendors-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => doCleanup(true)} data-testid="maint-clean-vendors-confirm">
                Yes, remove vendors
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={cleaning || !(stats?.seeded_users || stats?.seeded_vendors)} data-testid="maint-clean-all-btn">
              {cleaning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
              Clean ALL seeded data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove all seeded vendors AND consumers?</AlertDialogTitle>
              <AlertDialogDescription>
                Deletes ~{stats?.seeded_users?.toLocaleString?.()} users and{" "}
                {stats?.seeded_vendors?.toLocaleString?.()} vendor profiles plus all derived
                data (reviews, chats, chat messages, orders, cart items, sessions).
                QA fixtures and your superadmin are preserved. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="maint-clean-all-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => doCleanup(false)} data-testid="maint-clean-all-confirm">
                Yes, wipe seeded data
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {stats && (
        <p className="text-[11px] text-muted-foreground mt-4">
          Preserved fixtures: {stats.preserved_user_ids.join(", ")} · {stats.preserved_vendor_ids.join(", ")}
        </p>
      )}
      <DocsDownloadCard />
    </Card>
  );
}

/**
 * DocsDownloadCard — surfaces the build-prompt files stored under
 * /app/memory/ so the superadmin can hand them to external agents /
 * dev teams without SSHing into the server.
 */
function DocsDownloadCard() {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/docs`;
  const docs = [
    {
      id: "android-prompt",
      title: "Android Build Prompt",
      desc: "14-section spec for a Kotlin + Jetpack Compose Android app that mirrors this web app and reuses every existing /api endpoint.",
      size: "~18 KB",
    },
  ];

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-border/60" data-testid="maint-docs-card">
      <h2 className="font-heading text-xl flex items-center gap-2">
        <FileText className="h-5 w-5" /> Build prompts &amp; docs
      </h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
        Public download links — share with an Android team, another AI agent (Cursor / Copilot / Emergent), or an agency.
        No login required to fetch the file.
      </p>
      <div className="mt-5 space-y-3">
        {docs.map((d) => {
          const dl = `${base}/${d.id}`;
          const preview = `${base}/${d.id}?inline=true`;
          const txt = `${base}/${d.id}.txt`;
          return (
            <div
              key={d.id}
              className="rounded-xl border border-border/60 p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
              data-testid={`maint-doc-${d.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium">{d.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{d.desc}</div>
                <div className="text-[11px] text-muted-foreground/80 mt-1">
                  <code className="px-1 rounded bg-muted">{dl}</code> · {d.size}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button
                  asChild
                  size="sm"
                  className="rounded-full h-8"
                  data-testid={`maint-doc-${d.id}-download`}
                >
                  <a href={dl} download>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download .md
                  </a>
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="rounded-full h-8"
                  data-testid={`maint-doc-${d.id}-txt`}
                >
                  <a href={txt} download>
                    <Download className="h-3.5 w-3.5 mr-1" /> .txt
                  </a>
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  className="rounded-full h-8"
                  data-testid={`maint-doc-${d.id}-preview`}
                >
                  <a href={preview} target="_blank" rel="noreferrer">
                    Preview →
                  </a>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="rounded-full h-8"
                  onClick={() => copyLink(dl)}
                  data-testid={`maint-doc-${d.id}-copy`}
                  aria-label="Copy link"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
